<script setup>
import { displayPhone } from '@/plugins/misc'
/**
 * A saved contact, wherever the member meets one: the restaurant's My Notes,
 * the notes pop-up, and their own Favorites row.
 *
 * One component on purpose (Kapil, 2026-09-23: "should follow the same exact
 * format of the restaurant profile page… with these updates on both"). The
 * Favorites row used to show only the words, so a member who had photographed
 * a card had to open the restaurant to see it again — steps to reach something
 * they already saved.
 *
 * The line is `Caden Whitfield - General Manager - (312) 555-0147 -
 * caden@donangie.test`: the name from the CARD, and the number and email as
 * ACTIONS, because that is the only reason a member saved them. The `tel:` href
 * is built from the digits rather than the pretty form, and `.stop` keeps a tap
 * on a Favorites row from opening the note pop-up underneath it.
 *
 * The order is the person, then what the member said about them (the default
 * slot), then the card, then the entry's status (the `after` slot) — Kapil,
 * 2026-09-23: "Ben Heim - Bar Manager - cell - email / My note / Card / Private
 * Only - 0m ago", with the person reading "like a subject heading". The status
 * describes the whole entry, so it closes it, as the meta line does on My Notes.
 */
import { computed, ref, watch } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'

const props = defineProps({
  contact: { type: Object, default: null },
  cardImageId: { type: String, default: null },
})

const emit = defineEmits(['open-card'])

// The card's name is the one the restaurant knows them by; the member's own
// word for them is what RM SAYS, not what a saved record shows.
const displayName = computed(() => props.contact?.cardName || props.contact?.name || '')

const parts = computed(() => [displayName.value, props.contact?.role].filter(Boolean))

/**
 * A thumbnail that cannot load is dropped rather than left as a broken frame.
 * Resets when the card changes, so one bad image does not hide the next.
 */
const thumbFailed = ref(false)

watch(() => props.cardImageId, () => { thumbFailed.value = false })

const telHref = computed(() => {
  const raw = String(props.contact?.phone || '').trim()
  if (!raw) return ''
  const digits = raw.replace(/[^\d+]/g, '')

  return digits ? `tel:${digits}` : ''
})
</script>

<template>
  <div
    v-if="contact"
    class="contact-line"
  >
    <span
      v-for="(part, i) in parts"
      :key="`p-${i}`"
    >{{ i ? ' - ' : '' }}<span class="contact-line__who">{{ part }}</span></span>
    <template v-if="contact.phone">
      <span>{{ parts.length ? ' - ' : '' }}</span>
      <a
        :href="telHref"
        class="contact-line__action"
        @click.stop
      >{{ displayPhone(contact.phone) }}</a>
    </template>
    <template v-if="contact.email">
      <span>{{ parts.length || contact.phone ? ' - ' : '' }}</span>
      <a
        :href="`mailto:${contact.email}`"
        class="contact-line__action"
        @click.stop
      >{{ contact.email }}</a>
    </template>
  </div>

  <!-- What the member said about them — between who they are and the card. -->
  <slot v-if="contact" />

  <!-- The card they photographed, kept with the person it created. -->
  <button
    v-if="cardImageId && !thumbFailed"
    type="button"
    class="contact-line__card"
    :aria-label="`Open the saved card for ${displayName || 'this contact'}`"
    @click.stop="emit('open-card', cardImageId)"
  >
    <img
      :src="mediaUrl(cardImageId, 'thumb')"
      alt="Saved business card"
      @error="thumbFailed = true"
    >
  </button>

  <!-- What the entry is and when — it describes the whole entry, so it closes it. -->
  <slot
    v-if="contact"
    name="after"
  />
</template>

<style scoped>
.contact-line {
  margin-top: 4px;
  font-size: 14px;
  color: var(--ds-text-body, rgb(var(--v-theme-on-surface)));
}

/* Who they are reads as the entry's heading: name and role in the ink, one
   weight up; the number and email stay actions in the accent. */
.contact-line__who {
  font-weight: 600;
  color: var(--ds-brand-ink, rgb(var(--v-theme-on-surface)));
}

/* An action reads as one: the app's accent, and nothing else added. */
.contact-line__action {
  color: var(--ds-accent, rgb(var(--v-theme-primary)));
  text-decoration: none;
}

.contact-line__action:hover,
.contact-line__action:focus-visible {
  text-decoration: underline;
}

.contact-line__card {
  display: block;
  margin-top: 8px;
  padding: 6px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  max-width: 260px;
}

.contact-line__card img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 6px;
}
</style>
